import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3 } from 'lucide-react';
import { api } from '@/lib/api';
import { useConfigPage, BackLink } from './_shared';

/**
 * ServerStats-Config — the bot's ServerStats cron reads three channel IDs
 * from `moduleConfigs.serverStats.{members,bots,boosters}.channelId` and
 * updates their names every minute. Setup means: create the category + 3
 * voice channels + persist the IDs.
 *
 * That's a side-effect action, not just a config edit — so we call a
 * dedicated bot dispatch endpoint instead of PATCHing settings directly.
 */
interface StatsConfig {
  members?: { channelId: string };
  bots?: { channelId: string };
  boosters?: { channelId: string };
}

export function ServerStatsConfig() {
  const { guildId, settingsQuery, currentConfig } = useConfigPage('serverStats');
  const qc = useQueryClient();

  const config = currentConfig as StatsConfig;
  const configured = !!(config.members?.channelId || config.bots?.channelId);

  const setupMut = useMutation({
    mutationFn: () =>
      api('/api/actions/setup-server-stats', {
        method: 'POST',
        body: JSON.stringify({ guildId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', guildId] });
      qc.invalidateQueries({ queryKey: ['guildSettings', guildId] });
    },
  });

  if (settingsQuery.isLoading) return <p className="text-muted-foreground">Lade…</p>;

  return (
    <div className="mx-auto max-w-2xl">
      <BackLink guildId={guildId} />
      <h1 className="mb-6 text-2xl font-semibold">Server-Stats — Konfiguration</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" /> Live-Zähler
          </CardTitle>
          <CardDescription>
            Erstellt eine „📊 SERVER STATS"-Kategorie mit 3 Voice-Channels
            (Mitglieder, Bots, Boosters), deren Namen alle 60 Sekunden
            aktualisiert werden.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {configured ? (
            <div className="space-y-2">
              <p className="text-sm">Aktuell verwendete Channels:</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>👤 Mitglieder: <code>{config.members?.channelId ?? '—'}</code></li>
                <li>🤖 Bots: <code>{config.bots?.channelId ?? '—'}</code></li>
                <li>🌟 Boosters: <code>{config.boosters?.channelId ?? '—'}</code></li>
              </ul>
              <p className="pt-2 text-xs text-muted-foreground">
                Wenn du „Neu einrichten" klickst, werden neue Channels angelegt
                — die alten musst du manuell in Discord löschen, falls du sie
                nicht mehr brauchst.
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Noch nicht eingerichtet.</p>
          )}

          <div className="flex items-center justify-end">
            <Button
              disabled={setupMut.isPending}
              onClick={() => {
                if (
                  configured &&
                  !confirm(
                    'Server-Stats sind bereits eingerichtet. Wirklich neue Channels erstellen?'
                  )
                ) {
                  return;
                }
                setupMut.mutate();
              }}
            >
              {setupMut.isPending
                ? 'Erstelle…'
                : configured
                  ? 'Neu einrichten'
                  : 'Server-Stats einrichten'}
            </Button>
          </div>
          {setupMut.isSuccess && (
            <p className="text-sm text-primary">
              Request an Bot geschickt. Kanäle erscheinen in Sekunden — refresh die Seite um sie zu sehen.
            </p>
          )}
          {setupMut.error && (
            <p className="text-sm text-destructive">
              Fehler: {(setupMut.error as Error).message}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
